import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Typography from './Typography';
import Button from './Button/Button';
import FilterChip from './FilterChip';
import Modal from './Modal/Modal';
import AgendaFiltersModal from './AgendaFiltersModal';
import AgendaSearch from './AgendaSearch';
import HistoryDropdown from './HistoryDropdown';
import FilterSolidIcon from './Icons/FilterSolidIcon';
import { theme } from '../theme';
import { useAttendeeStateTranslations } from '../constants/attendeeStates';
import { EXPERIENCE_LEVELS, TICKET_FREE } from '../constants/eventTypes';
import useWindowDimensions from '../hooks/useWindowDimensions';

// import RevertIcon from './Icons/RevertIcon';

const AgendaFilters = ({
  currentView,
  onViewChange,
  secondaryFilters,
  onSecondaryFiltersChange,
  searchTerm,
  onSearchChange,
  onSearchClear,
  availableEventTypes = [],
  availableVehicles = [],
  availableTerrainTypes = [],
  currentViewEvents
}) => {
  const { t } = useTranslation('agenda');
  const { getStateLabel } = useAttendeeStateTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    enrollmentStatus: secondaryFilters.enrollmentStatus || [],
    eventType: secondaryFilters.eventType || [],
    vehicle: secondaryFilters.vehicle || null,
    terrainType: secondaryFilters.terrainType || [],
    price: secondaryFilters.price || [],
    difficulty: secondaryFilters.difficulty || [],
    showOrganizedEvents: secondaryFilters.showOrganizedEvents !== undefined ? secondaryFilters.showOrganizedEvents : true
  });

  // Hook para dimensiones de pantalla
  const { width } = useWindowDimensions();
  const isMobile = width < parseInt(theme.breakpoints.md);
  const isTablet = width < parseInt(theme.breakpoints.lg); // Ajustar breakpoint según sea necesari


  // Calcular el número total de filtros secundarios activos (excluyendo valores por defecto)
  const getActiveFiltersCount = () => {
    let count = 0;

    // Contar filtros de array
    if (secondaryFilters.enrollmentStatus?.length > 0) count += secondaryFilters.enrollmentStatus.length;
    if (secondaryFilters.eventType?.length > 0) count += secondaryFilters.eventType.length;
    if (secondaryFilters.terrainType?.length > 0) count += secondaryFilters.terrainType.length;
    if (secondaryFilters.price?.length > 0) count += secondaryFilters.price.length;
    if (secondaryFilters.difficulty?.length > 0) count += secondaryFilters.difficulty.length;

    // Contar filtros individuales
    if (secondaryFilters.vehicle) count += 1;
    if (secondaryFilters.showOrganizedEvents === false) count += 1; // Solo contar si está desactivado

    return count;
  };

  const handleOpenModal = () => {
    // Asegurar que todas las propiedades de array estén definidas
    const safeFilters = {
      enrollmentStatus: secondaryFilters.enrollmentStatus || [],
      eventType: secondaryFilters.eventType || [],
      vehicle: secondaryFilters.vehicle || null,
      terrainType: secondaryFilters.terrainType || [],
      price: secondaryFilters.price || [],
      difficulty: secondaryFilters.difficulty || [],
      showOrganizedEvents: secondaryFilters.showOrganizedEvents !== undefined ? secondaryFilters.showOrganizedEvents : true
    };
    setTempFilters(safeFilters);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleApplyFilters = () => {
    onSecondaryFiltersChange(tempFilters);
    setIsModalOpen(false);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      enrollmentStatus: [],
      eventType: [],
      vehicle: null,
      terrainType: [],
      price: [],
      difficulty: [],
      showOrganizedEvents: true
    };
    setTempFilters(resetFilters);
    onSecondaryFiltersChange(resetFilters);
    setIsModalOpen(false);
  };

  const removeFilter = (filterType, value) => {
    if (Array.isArray(secondaryFilters[filterType])) {
      const newValues = secondaryFilters[filterType].filter(item => item !== value);
      onSecondaryFiltersChange({
        ...secondaryFilters,
        [filterType]: newValues
      });
    } else {
      const defaultValue = filterType === 'showOrganizedEvents' ? true : null;
      onSecondaryFiltersChange({
        ...secondaryFilters,
        [filterType]: defaultValue
      });
    }
  };

  const getFilterLabel = (filterType, value) => {
    switch (filterType) {
      case 'enrollmentStatus':
        return getStateLabel(value);
      case 'eventType':
        return t(`filters.eventType.${value}`, value);
      case 'vehicle':
        const vehicle = availableVehicles.find(v => v.id === value);
        return vehicle ? `${vehicle.brand} ${vehicle.model}` : value;
      case 'terrainType':
        return t(`filters.terrainType.${value}`, value);
      case 'price':
        return t(`filters.price.${value}`, value === TICKET_FREE ? 'Gratis' : 'De pago');
      case 'difficulty':
        return t(`filters.difficulty.${value}`,
          value === EXPERIENCE_LEVELS[0] ? 'Principiante' :
            value === EXPERIENCE_LEVELS[1] ? 'Intermedio' : 'Avanzado'
        );
      case 'showOrganizedEvents':
        return value ? 'Mostrar eventos organizados' : 'Ocultar eventos organizados';
      default:
        return value;
    }
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <FiltersContainer>
      {isTablet ? (
        <FiltersControlsMobile>
          <AgendaSearch
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            onClearSearch={onSearchClear}
          />
          <FiltersRow>
            <FilterButton
              $variant="outline"
              onClick={handleOpenModal}
              size="l"
              sizeTablet="l"
              sizeMobile="m">
              <FilterSolidIcon fill={theme.colors.defaultMain} width={16} height={16} />
              <Typography $variant="body-2-medium" color={theme.colors.defaultMain}>
                {t('filters.button', 'Filtros')}
              </Typography>
              {activeFiltersCount > 0 && (
                <FilterCounter>
                  <Typography $variant="caption-semibold" $align="center" color={theme.colors.inverseMain}>
                    {activeFiltersCount}
                  </Typography>
                </FilterCounter>
              )}
            </FilterButton>
            <HistoryDropdown
              value={currentView || 'upcoming'}
              onChange={onViewChange}
            />
          </FiltersRow>
        </FiltersControlsMobile>
      ) : (
        <FiltersControls>
          {/* <FiltersHeader> */}
          <FilterButton
            $variant="outline"
            onClick={handleOpenModal}
            size="l"
            sizeTablet="l"
            sizeMobile="m">
            <FilterSolidIcon fill={theme.colors.defaultMain} width={20} height={20} />
            <Typography $variant="body-2-medium" color={theme.colors.defaultMain}>
              {t('filters.button', 'Filtros')}
            </Typography>
            {activeFiltersCount > 0 && (
              <FilterCounter>
                <Typography $variant="caption-semibold" $align="center" color={theme.colors.inverseMain}>
                  {activeFiltersCount}
                </Typography>
              </FilterCounter>
            )}
          </FilterButton>
          {/* </FiltersHeader> */}

          {/* Componente de búsqueda y navegación principal */}
          <SearchAndHistoryContainer>
            <AgendaSearch
              searchTerm={searchTerm}
              onSearchChange={onSearchChange}
              onClearSearch={onSearchClear}
            />
            {/* Dropdown de navegación principal (próximos/pasados) */}
            <HistoryDropdown
              value={currentView || 'upcoming'}
              onChange={onViewChange}
            />
          </SearchAndHistoryContainer>
        </FiltersControls>
      )}

      {/* <FiltersControls>
        <FiltersHeader>
          <FilterButton onClick={handleOpenModal}>
            <FilterSolidIcon fill={theme.colors.defaultMain} width={20} height={20} />
            <Typography $variant="body-2-medium" color={theme.colors.defaultMain}>
              {t('filters.button', 'Filtros')}
            </Typography>
            {activeFiltersCount > 0 && (
              <FilterCounter>
                <Typography $variant="overline-semibold" color={theme.colors.inverseMain}>
                  {activeFiltersCount}
                </Typography>
              </FilterCounter>
            )}
          </FilterButton>
        </FiltersHeader>

        <SearchAndHistoryContainer>
          <AgendaSearch
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            onClearSearch={onSearchClear}
          />
          <HistoryDropdown
            value={currentView || 'upcoming'}
            onChange={onViewChange}
          />
        </SearchAndHistoryContainer>
      </FiltersControls> */}

      {/* Chips de filtros secundarios activos */}
      {activeFiltersCount > 0 && (
        <ActiveFiltersContainer>
          {/* Mostrar chips para cada filtro secundario activo */}
          {Object.keys(secondaryFilters).map(filterType => {
            if (Array.isArray(secondaryFilters[filterType])) {
              return secondaryFilters[filterType].map(value => (
                <FilterChip
                  key={`${filterType}-${value}`}
                  category={getFilterLabel(filterType, value)}
                  isActive={true}
                  onRemove={() => removeFilter(filterType, value)}
                />
              ));
            } else if (secondaryFilters[filterType] && secondaryFilters[filterType] !== '' && secondaryFilters[filterType] !== true) {
              return (
                <FilterChip
                  key={`${filterType}-${secondaryFilters[filterType]}`}
                  category={getFilterLabel(filterType, secondaryFilters[filterType])}
                  isActive={true}
                  onRemove={() => removeFilter(filterType, secondaryFilters[filterType])}
                />
              );
            }
            return null;
          })}
          {activeFiltersCount > 0 && (
            <ResetButton onClick={handleResetFilters} $variant="outline" size="m" sizeTablet="m" sizeMobile="m">
              {/* <Typography $variant="body-3-medium" color={theme.colors.defaultSubtle}>
                {t('filters.reset', 'Limpiar filtros')}
              </Typography> */}
              {/* <RevertIcon fill={theme.colors.defaultSubtle} width={16} height={16} /> */}
              {t('filters.reset', 'Limpiar filtros')}
            </ResetButton>
          )}
        </ActiveFiltersContainer>
      )}

      {/* Modal de filtros secundarios */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={t('filters.modal.title', 'Filtros')}
      >
        <ModalContent>
          <ScrollableContent>
            <AgendaFiltersModal
              tempFilters={tempFilters}
              onTempFiltersChange={setTempFilters}
              availableEventTypes={availableEventTypes}
              availableVehicles={availableVehicles}
              availableTerrainTypes={availableTerrainTypes}
              myEvents={currentViewEvents}
            />
          </ScrollableContent>

          <ModalActions>
            <Button
              $variant="outline"
              size="l"
              sizeTablet="l"
              sizeMobile="m"
              onClick={handleResetFilters}
            >
              {t('filters.modal.reset', 'Resetear filtros')}
            </Button>
            <Button
              $variant=""
              size="l"
              sizeTablet="l"
              sizeMobile="m"
              onClick={handleApplyFilters}
            >
              {t('filters.modal.apply', 'Aplicar filtros')}
            </Button>
          </ModalActions>
        </ModalContent>
      </Modal>
    </FiltersContainer>
  );
};

export default AgendaFilters;

const SearchAndHistoryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  flex: 1;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const FiltersControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  width: 100%;
`;

const FiltersControlsMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: space-between;
  width: 100%;
`;

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

// const FilterButton = styled.button`
//   display: flex;
//   align-items: center;
//   gap: ${({ theme }) => theme.sizing.xs};
//   padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
//   background: ${({ theme }) => theme.fill.defaultMain};
//   border: 1px solid ${({ theme }) => theme.border.defaultWeak};
//   border-radius: ${({ theme }) => theme.radius.xs};
//   cursor: pointer;
//   transition: all 0.2s ease;
//   position: relative;

//   &:hover {
//     background: ${({ theme }) => theme.fill.defaultSubtle};
//   }
// `;

const FilterButton = styled(Button)``;

const FilterCounter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: ${({ theme }) => theme.fill.brandMain};
  border-radius: 10px;
`;

const ResetButton = styled(Button)`
  // display: flex;
  // align-items: center;
  // gap: ${({ theme }) => theme.sizing.xxs};
  // padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  // background: transparent;
  // border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  // border-radius: ${({ theme }) => theme.radius.xs};
  // cursor: pointer;
  // transition: all 0.2s ease;

  // &:hover {
  //   background: ${({ theme }) => theme.fill.defaultWeak};
  // }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  // gap: ${({ theme }) => theme.sizing.sm};
  padding: 0;
  height: 100%;
  overflow: hidden;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultMain};
  position: sticky;
  bottom: 0;
  z-index: 10;
  margin-top: auto;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;